import React, { useState, useEffect } from 'react';
import { createTraining } from '../../../../api/adminTrainingApi';
import { createTrainingVideo } from '../../../../api/trainingVideoApi';
import { toast } from 'react-toastify';
import { fetchExamTypes } from '../../../../api/examApi';
import { fetchCategories } from '../../../../api/categoryApi';
import './TrainingDashboard.css';

const TrainingDashboard = () => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [formData, setFormData] = useState({
        trainingName: '',
        trainingDescription: '',
        trainingDuration: '',
        examTypeId: '',
        categoryType: '',
        videos: [
            {
                contentType: 'Video',
                videoUrl: '',
                videoTitle: '',
                videoDesc: '',
                videoDuration: '',
                mandatoryDuration: '',
                videoThumbnailUrl: '',
                language: 'English'
            }
        ]
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [examTypes, setExamTypes] = useState([]);
    const [allCategories, setAllCategories] = useState([]);

    useEffect(() => {
        const loadInitialData = async () => {
            try {
                const [categories] = await Promise.all([
                    fetchCategories()
                ]);
                setAllCategories(categories);
            } catch (error) {
                console.error('Error loading data:', error);
                toast.error('Failed to load training types');
            }
        };
        loadInitialData();
    }, []);

    useEffect(() => {
        const loadExamTypes = async () => {
            try {
                const types = await fetchExamTypes();
                setExamTypes(types);
            } catch (error) {
                console.error('Error loading exam types:', error);
                toast.error('Failed to load training types');
            }
        };
        loadExamTypes();
    }, []);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleVideoChange = (index, e) => {
        const { name, value } = e.target;
        const updatedVideos = [...formData.videos];
        updatedVideos[index][name] = value;
        setFormData({ ...formData, videos: updatedVideos });
    };

    const addVideoEntry = () => {
        setFormData({
            ...formData,
            videos: [
                ...formData.videos,
                {
                    contentType: 'Video',
                    videoUrl: '',
                    videoTitle: '',
                    videoDesc: '',
                    videoDuration: '',
                    mandatoryDuration: '',
                    videoThumbnailUrl: '',
                    language: 'English'
                }
            ]
        });
    };

    const removeVideoEntry = (index) => {
        const updatedVideos = formData.videos.filter((_, i) => i !== index);
        setFormData({ ...formData, videos: updatedVideos });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            const trainingResponse = await createTraining({
                trainingId: 0,
                trainingName: formData.trainingName,
                trainingDescription: formData.trainingDescription,
                trainingDuration: formData.trainingDuration,
                examTypeId: parseInt(formData.examTypeId),
                url: 'NA'
            });

            if (trainingResponse.trainingId) {
                await Promise.all(
                    formData.videos.map((video) =>
                        createTrainingVideo({
                            trainingVideoId: 0,
                            trainingId: trainingResponse.trainingId,
                            videoSequenceId: 0,
                            videoThumbnailUrl: video.videoThumbnailUrl || 'string',
                            videoUrl: video.videoUrl,
                            videoDesc: video.videoDesc,
                            videoDuration: parseInt(video.videoDuration),
                            mandatoryDuration: parseInt(video.mandatoryDuration),
                            videoTitle: video.videoTitle,
                            examType: {
                                examTypeId: parseInt(formData.examTypeId),
                                examTypeDesc: 'string'
                            },
                            category: {
                                categoryId: parseInt(formData.categoryType),
                                examType: {
                                    examTypeId: parseInt(formData.examTypeId),
                                    examTypeDesc: 'string'
                                },
                                categoryName: 'string'
                            },
                            contentType: video.contentType,
                            language: video.language.toUpperCase()
                        })
                    )
                );

                setIsSubmitting(false);
                setIsModalOpen(false);
                setFormData({
                    trainingName: '',
                    trainingDescription: '',
                    trainingDuration: '',
                    examTypeId: '',
                    categoryType: '',
                    videos: [
                        {
                            contentType: 'Video',
                            videoUrl: '',
                            videoTitle: '',
                            videoDesc: '',
                            videoDuration: '',
                            mandatoryDuration: '',
                            videoThumbnailUrl: '',
                            language: 'English'
                        }
                    ]
                });
                toast.success('Training created successfully!');
                setTimeout(() => {
                    window.location.reload();
                }, 2000);
            }
        } catch (error) {
            setIsSubmitting(false);
            console.error('Error creating training:', error);
            toast.error('Failed to create training. Please try again.');
        }
    };

    return (
        <div className="admin-training-dashboard">
            <button
                className="admin-training-dashboard__create-btn"
                onClick={() => setIsModalOpen(true)}
            >
                Create Training
            </button>

            {isModalOpen && (
                <div className="admin-training-dashboard__modal">
                    <div className="admin-training-dashboard__modal-content">
                        <h2 className='admin-training-dashboard__modal-content-h2'>Create New Training</h2>
                        <form onSubmit={handleSubmit}>
                            <div className="admin-training-dashboard__form-group">
                                <label htmlFor="trainingName">Training Name</label>
                                <input
                                    id="trainingName"
                                    type="text"
                                    name="trainingName"
                                    value={formData.trainingName}
                                    onChange={handleInputChange}
                                    required
                                />
                            </div>
                            <div className="admin-training-dashboard__form-group">
                                <label htmlFor="trainingDescription">Training Description</label>
                                <textarea
                                    id="trainingDescription"
                                    name="trainingDescription"
                                    value={formData.trainingDescription}
                                    onChange={handleInputChange}
                                    required
                                />
                            </div>
                            <div className="admin-training-dashboard__form-group">
                                <label htmlFor="trainingDuration">Training Duration</label>
                                <input
                                    id="trainingDuration"
                                    type="number"
                                    name="trainingDuration"
                                    value={formData.trainingDuration}
                                    onChange={handleInputChange}
                                    required
                                />
                            </div>
                            <div className="admin-training-dashboard__flex-row">
                                <div className="admin-training-dashboard__form-group">
                                    <label htmlFor="examTypeId">Training Type</label>
                                    <select
                                        id="examTypeId"
                                        name="examTypeId"
                                        value={formData.examTypeId}
                                        onChange={handleInputChange}
                                        required
                                    >
                                        <option value="">Select Training Type</option>
                                        {examTypes.map((type) => (
                                            <option key={type.examTypeId} value={type.examTypeId}>
                                                {type.examTypeDesc}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="admin-training-dashboard__form-group">
                                    <label htmlFor="categoryType">Category Type</label>
                                    <select
                                        id="categoryType"
                                        name="categoryType"
                                        value={formData.categoryType}
                                        onChange={handleInputChange}
                                    >
                                        <option value="">Select Category</option>
                                        {allCategories
                                            .filter(cat => cat.examType.examTypeId === parseInt(formData.examTypeId))
                                            .map(cat => (
                                                <option key={cat.categoryId} value={cat.categoryId}>
                                                    {cat.categoryName}
                                                </option>
                                            ))
                                        }
                                    </select>
                                </div>
                            </div>

                            {formData.videos.map((video, index) => (
                                <div key={index} className="admin-training-dashboard__video-entry">
                                    <h3 className='videoSequence-admin-training-h3'>
                                        {/* The header showing the item number */}
                                        Content {index + 1}{' '}
                                        {/* The content-type dropdown right next to the heading */}
                                        <select
                                            name="contentType"
                                            value={video.contentType}
                                            onChange={(e) => handleVideoChange(index, e)}
                                        >
                                            <option value="Video">Video</option>
                                            <option value="PDF">PDF</option>
                                        </select>
                                    </h3>

                                    {video.contentType === 'Video' ? (
                                        <>
                                            <div className="admin-training-dashboard__form-group">
                                                <label htmlFor={`language${index}`}>Language</label>
                                                <select
                                                    id={`language${index}`}
                                                    name="language"
                                                    value={video.language}
                                                    onChange={(e) => handleVideoChange(index, e)}
                                                    required
                                                >
                                                    <option value="">Select Language</option>
                                                    <option value="English">English</option>
                                                    <option value="Hindi">Hindi</option>
                                                </select>
                                            </div>
                                            <div className="admin-training-dashboard__form-group">
                                                <label htmlFor={`videoUrl${index}`}>Video URL</label>
                                                <input
                                                    id={`videoUrl${index}`}
                                                    type="text"
                                                    name="videoUrl"
                                                    value={video.videoUrl}
                                                    onChange={(e) => handleVideoChange(index, e)}
                                                    required
                                                />
                                            </div>
                                            <div className="admin-training-dashboard__form-group">
                                                <label htmlFor={`videoTitle${index}`}>Video Title</label>
                                                <input
                                                    id={`videoTitle${index}`}
                                                    type="text"
                                                    name="videoTitle"
                                                    value={video.videoTitle}
                                                    onChange={(e) => handleVideoChange(index, e)}
                                                    required
                                                />
                                            </div>
                                            <div className="admin-training-dashboard__form-group">
                                                <label htmlFor={`videoDesc${index}`}>Video Description</label>
                                                <textarea
                                                    id={`videoDesc${index}`}
                                                    name="videoDesc"
                                                    value={video.videoDesc}
                                                    onChange={(e) => handleVideoChange(index, e)}
                                                    required
                                                />
                                            </div>
                                            <div className="admin-training-dashboard__form-group">
                                                <label htmlFor={`videoDuration${index}`}>Video Duration</label>
                                                <input
                                                    id={`videoDuration${index}`}
                                                    type="number"
                                                    name="videoDuration"
                                                    value={video.videoDuration}
                                                    onChange={(e) => handleVideoChange(index, e)}
                                                    required
                                                />
                                            </div>
                                            <div className="admin-training-dashboard__form-group">
                                                <label htmlFor={`mandatoryDuration${index}`}>Mandatory Duration</label>
                                                <input
                                                    id={`mandatoryDuration${index}`}
                                                    type="number"
                                                    name="mandatoryDuration"
                                                    value={video.mandatoryDuration}
                                                    onChange={(e) => handleVideoChange(index, e)}
                                                    required
                                                />
                                            </div>
                                            <div className="admin-training-dashboard__form-group">
                                                <label htmlFor={`videoThumbnailUrl${index}`}>Video Thumbnail URL</label>
                                                <input
                                                    id={`videoThumbnailUrl${index}`}
                                                    type="text"
                                                    name="videoThumbnailUrl"
                                                    value={video.videoThumbnailUrl}
                                                    onChange={(e) => handleVideoChange(index, e)}
                                                />
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <div className="admin-training-dashboard__form-group">
                                                <label htmlFor={`videoUrl${index}`}>PDF URL</label>
                                                <input
                                                    id={`videoUrl${index}`}
                                                    type="text"
                                                    name="videoUrl"
                                                    value={video.videoUrl}
                                                    onChange={(e) => handleVideoChange(index, e)}
                                                    required
                                                />
                                            </div>
                                            <div className="admin-training-dashboard__form-group">
                                                <label htmlFor={`videoTitle${index}`}>PDF Title</label>
                                                <input
                                                    id={`videoTitle${index}`}
                                                    type="text"
                                                    name="videoTitle"
                                                    value={video.videoTitle}
                                                    onChange={(e) => handleVideoChange(index, e)}
                                                    required
                                                />
                                            </div>
                                            <div className="admin-training-dashboard__form-group">
                                                <label htmlFor={`videoDesc${index}`}>PDF Description</label>
                                                <textarea
                                                    id={`videoDesc${index}`}
                                                    name="videoDesc"
                                                    value={video.videoDesc}
                                                    onChange={(e) => handleVideoChange(index, e)}
                                                    required
                                                />
                                            </div>
                                            <div className="admin-training-dashboard__form-group">
                                                <label htmlFor={`videoThumbnailUrl${index}`}>PDF Thumbnail URL</label>
                                                <input
                                                    id={`videoThumbnailUrl${index}`}
                                                    type="text"
                                                    name="videoThumbnailUrl"
                                                    value={video.videoThumbnailUrl}
                                                    onChange={(e) => handleVideoChange(index, e)}
                                                />
                                            </div>
                                        </>
                                    )}

                                    <button
                                        type="button"
                                        className='admin-remove-btn-training'
                                        onClick={() => removeVideoEntry(index)}
                                    >
                                        ⛔ Remove Content {index + 1}
                                    </button>
                                </div>
                            ))}
                            <div className="admin-add-video-parent-container">
                                <button
                                    type="button"
                                    className='admin-add-video-btn'
                                    onClick={addVideoEntry}
                                >
                                    ✙ Add Content
                                </button>
                            </div>

                            <div className="admin-training-dashboard__modal-actions">
                                <button type="submit" disabled={isSubmitting}>
                                    {isSubmitting ? 'Creating Training...' : 'Create Training'}
                                </button>
                                <button type="button" onClick={() => setIsModalOpen(false)}>Cancel</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TrainingDashboard;
